<template>
  <div class="bonus-tracker">
    <van-row :wrap="false" class="tracker-row">
      
      <div 
        v-if="dayList && dayList.length > 0"
        class="progress-bar-bg" 
        :style="{
          left: `0`,
          right: `0`
        }"
      >
        <van-progress 
          :percentage="progressPercentage" 
          :show-pivot="false" 
          color="#f5c84c" 
          track-color="#1a1a2e" 
          stroke-width="10px" 
        />
      </div>

      <van-col
        v-for="(item, index) in dayList"
        :key="index"
        class="tracker-col"
      >
        <div :class="['chest-container', { active: activeDay === item.reward_day }]">
          
          <div class="icon-wrapper">
            <img 
              :src="icons[Math.min(index, 4)]" 
              class="chest-icon" 
            />
            
            <div v-if="item.reward_data?.rewardCoin" class="claimed-badge">
              <van-icon name="success" />
            </div>
          </div>
          
          <div class="day-label" @click="$emit('select', item)">
            {{ $t('common.travel') }} {{ item.reward_day }}
          </div>
          
          <div class="arrow-icon" v-if="activeDay === item.reward_day">
            <img style="width: 15px;" src="@/assets/withdraw/arrowMy.png" alt="arrow">
          </div>
          
        </div>
      </van-col>
    </van-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import icon1 from '@/assets/themeFour/DayIcon.png'
import icon2 from '@/assets/themeFour/DayIcon2.png'
import icon3 from '@/assets/themeFour/DayIcon3.png'
import icon4 from '@/assets/themeFour/DayIcon4.png'
import icon5 from '@/assets/themeFour/DayIcon5.png'

const icons = [icon1, icon2, icon3, icon4, icon5]

const props = defineProps<{
  dayList: any[]
  activeDay: number | string
}>()

defineEmits(['select'])

const progressPercentage = computed(() => {
  if (!props.dayList || props.dayList.length <= 1) return 0;
  
  let lastActiveIndex = -1;
  
  // progressed to (0 = completed/expired, 1 = today)
  for (let i = props.dayList.length - 1; i >= 0; i--) {
    const status = props.dayList[i].reward_data?.activeStatus;
    if (status === 0 || status === 1) {
      lastActiveIndex = i;
      break;
    }
  }

  if (lastActiveIndex <= 0) return 0;
  return (lastActiveIndex / (props.dayList.length - 1)) * 100;
})
</script>

<style scoped>
.bonus-tracker {
  width: 100%;
  overflow-x: auto;
  padding-top: 20px;
  padding-left: 15px; 
  padding-right: 15px;
  box-sizing: border-box;
}

.bonus-tracker::-webkit-scrollbar {
  display: none;
}

.tracker-row {
  display: flex;
  align-items: flex-start; 
  min-width: max-content;
  padding: 20px 0 25px;
  position: relative;
  margin-top: -8%;
}

.progress-bar-bg {
  position: absolute;
  top: 55%;
  z-index: 0;
  transform: translateY(-50%);
}

.tracker-col {
  display: flex;
  justify-content: center; 
  flex: 1; 
  z-index: 1; 
}

.chest-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
}

.chest-container.active {
  opacity: 1; 
}

.icon-wrapper {
  position: relative;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 55px;
  height: 55px;
  z-index: 1;
}

.chest-container.active .icon-wrapper::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150%;
  height: 150%;
  background-image: url('@/assets/themeFour/actBg.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 0;
  pointer-events: none;
}

.chest-icon {
  height: 45px;
  object-fit: contain;
  z-index: 2;
  position: relative;
}

.claimed-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #4caf50;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 3;
}

.day-label {
  font-size: 13px;
  font-weight: 600;
  color: #937f9a;
  padding: 4px 12px;
  border-radius: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin-bottom: 0px;
  z-index: 1;
  background: #ffffff26;
  margin-top: 35px;
  cursor: pointer;
}

.chest-container.active .day-label {
  background:  #5C82F5;
  color: #ffffff;
}

.arrow-icon {
  position: absolute;
  bottom: -20px;
}


@media (max-width: 768px) {
  .tracker-col { min-width: 80px; }
  .icon-wrapper { width: 50px; height: 50px; margin-bottom: 12px; }
  .day-label { margin-top: 30px; }
  .chest-icon { height: 40px; }
  .claimed-badge { top: -2px; right: -2px; }
}

@media (max-width: 480px) {
  .tracker-col { min-width: 70px; }
  .icon-wrapper { width: 45px; height: 45px; margin-bottom: 12px; }
  .chest-icon { height: 35px; }
  .claimed-badge { top: -2px; right: -2px; width: 14px; height: 14px; font-size: 9px; }
  .day-label { font-size: 11px; padding: 3px 10px; margin-top: 25px; }
}

@media (max-width: 380px) {
  .tracker-col { min-width: 60px; }
  .icon-wrapper { width: 40px; height: 40px; margin-bottom: 10px; }
  .chest-icon { height: 30px; }
  .claimed-badge { top: -2px; right: -2px; width: 12px; height: 12px; font-size: 8px; }
  .day-label { font-size: 10px; padding: 2px 6px; margin-top: 20px; }
  .progress-bar-bg { top: 55%; }
  .arrow-icon { bottom: -20px; }
  .arrow-icon img { width: 12px !important; }
}

</style>